import type { IQueueFrame } from '@/types'
import {
  HOLD_UNTIL_READY_TIMEOUT_MS,
  HOLD_UNTIL_READY_VIDEO_TIMEOUT_MS
} from '@/constants/playback'

export { HOLD_UNTIL_READY_TIMEOUT_MS, HOLD_UNTIL_READY_VIDEO_TIMEOUT_MS }

/** Sticky hold timeout for the incoming media kind (video needs longer before `playing`). */
export function holdUntilReadyTimeoutMsForTargetKind(targetKind: string): number {
  if (targetKind === 'video-blob' || targetKind === 'hls') {
    return HOLD_UNTIL_READY_VIDEO_TIMEOUT_MS
  }
  return HOLD_UNTIL_READY_TIMEOUT_MS
}


/** Same creative identity for buffer hold / sticky overlay matching. */
export function sameQueueFrameIdentity(
  a: Pick<IQueueFrame, 'id' | 'playlistUrn' | 'playlistSeqNo'> | null | undefined,
  b: Pick<IQueueFrame, 'id' | 'playlistUrn' | 'playlistSeqNo'> | null | undefined
): boolean {
  if (a == null || b == null) {
    return false
  }
  return (
    a.id === b.id
    && a.playlistUrn === b.playlistUrn
    && (a.playlistSeqNo ?? 0) === (b.playlistSeqNo ?? 0)
  )
}

/** Stable facet→asset map for comparing same-identity content edits (#1352). */
export function queueFrameFacetAssetSignature(
  frame: Pick<IQueueFrame, 'facets'> | null | undefined
): string {
  if (frame == null) {
    return ''
  }
  const facets = frame.facets ?? {}
  return Object.keys(facets)
    .sort()
    .map((key) => {
      const asset = facets[key]?.asset
      return `${key}:${typeof asset === 'string' ? asset : ''}`
    })
    .join('|')
}

/** True when facet asset URNs differ (same frame id can still remount media after on-air edit). */
export function queueFramesDifferInFacetAssets(
  a: Pick<IQueueFrame, 'facets'> | null | undefined,
  b: Pick<IQueueFrame, 'facets'> | null | undefined
): boolean {
  return queueFrameFacetAssetSignature(a) !== queueFrameFacetAssetSignature(b)
}

/**
 * True when the incoming creative is already paint-ready at commit time — skip sticky hold.
 */
export function isIncomingPaintReadyAtCommit(opts: {
  nextKind: string
  nextImageSrc: string | null
  isImagePredecoded: (src: string) => boolean
  seamlessContinuous: boolean
}): boolean {
  const { nextKind, nextImageSrc, isImagePredecoded, seamlessContinuous } = opts
  if (nextKind === 'image') {
    return nextImageSrc != null && nextImageSrc !== '' && isImagePredecoded(nextImageSrc)
  }
  if (nextKind === 'video-blob' || nextKind === 'hls') {
    // Seamless continuous path skips deferred video reveal.
    return seamlessContinuous
  }
  return false
}

/** Progressive / HLS creatives that consume a MediaCodec (or MSE) session while `src` is set. */
export function isVideoPresentationMediaKind(kind: string): boolean {
  return kind === 'video-blob' || kind === 'hls'
}

/**
 * Skip sticky remount when both outgoing and incoming are video (#1443 / PLAYER-19).
 * Sticky mounts a third `<video>` on top of the double-buffer (+ preload), exceeding the
 * documented ≤2 decoder budget and wedging mid-tier Android (PIPELINE_ERROR_DECODE).
 */
export function shouldSkipStickyForVideoToVideo(opts: {
  prevKind: string
  nextKind: string
}): boolean {
  return isVideoPresentationMediaKind(opts.prevKind)
    && isVideoPresentationMediaKind(opts.nextKind)
}

/**
 * Sticky remounts the outgoing Vue vnode. Native Media3 pixels live on a SurfaceView under
 * the WebView — the placeholder is transparent, so a sticky overlay cannot hold the last
 * frame and can hide the underlay. Cut is OK (P1c). Incoming-native + outgoing still/widget
 * still uses sticky (image overlay until `onFirstFrame`).
 */
export function shouldSkipStickyForNativeHybridSurface(opts: {
  outgoingNative: boolean
}): boolean {
  return opts.outgoingNative === true
}

/**
 * Whether this presentation advance should use sticky-outgoing hold (Phase B).
 */
export function shouldStickyHoldPresentationAdvance(opts: {
  prevFrame: IQueueFrame | null | undefined
  nextFrame: IQueueFrame | null | undefined
  registrationHoldActive: boolean
  incomingAlreadyReady: boolean
}): boolean {
  const { prevFrame, nextFrame, registrationHoldActive, incomingAlreadyReady } = opts
  if (registrationHoldActive) {
    return false
  }
  if (prevFrame == null || nextFrame == null) {
    return false
  }
  /**
   * Same spine slot normally skips sticky (duration-only / metadata polls). On-air facet.asset
   * swaps still remount ImageContainer / VideoContainer — hold outgoing pixels until paint-ready.
   */
  if (
    sameQueueFrameIdentity(prevFrame, nextFrame)
    && !queueFramesDifferInFacetAssets(prevFrame, nextFrame)
  ) {
    return false
  }
  if (incomingAlreadyReady) {
    return false
  }
  return true
}

export type StickyHoldClearReason = 'ready' | 'timeout' | 'supersede' | 'teardown'

/**
 * Residual video→image blackout after sticky hold only on timeout (paint-ready path already revealed).
 */
export function shouldArmPostVideoBlackoutOnStickyHoldClear(opts: {
  reason: StickyHoldClearReason
  prevKind: string
  targetKind: string
  targetImageSrc: string | null
  shouldArmBlackout: (src: string | null) => boolean
}): boolean {
  return (
    opts.reason === 'timeout'
    && (opts.prevKind === 'video-blob' || opts.prevKind === 'hls')
    && opts.targetKind === 'image'
    && opts.shouldArmBlackout(opts.targetImageSrc)
  )
}
